<template>
  <TransitionRoot as="template" :show="!!show && !!order">
    <Dialog as="div" class="fixed inset-0 overflow-hidden z-50" @close="emitClose">
      <div class="absolute inset-0 overflow-hidden">
        <DialogOverlay class="absolute inset-0 bg-black opacity-40" />

        <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
          <TransitionChild
            as="template"
            enter="transform transition ease-in-out duration-500 sm:duration-700"
            enter-from="translate-x-full"
            enter-to="translate-x-0"
            leave="transform transition ease-in-out duration-500 sm:duration-700"
            leave-from="translate-x-0"
            leave-to="translate-x-full"
          >
            <div class="pointer-events-auto w-screen max-w-xl">
              <div class="flex h-full flex-col divide-y divide-slate-700 bg-slate-600 shadow-xl">
                <!-- HEADER -->
                <div class="h-0 flex-1 overflow-y-auto">
                  <div class="bg-gradient-to-r from-slate-900 to-slate-800 py-6 px-4 sm:px-6">
                    <div class="flex items-center justify-between">
                      <DialogTitle class="text-lg font-medium text-white">
                        Podgląd zamówienia paliwowego
                      </DialogTitle>
                    </div>
                    <div class="mt-1">
                      <p class="text-sm text-blue-300">
                        ID: <span class="font-mono text-xs">{{ order.id || order._id }}</span>
                      </p>
                    </div>
                  </div>

                  <!-- STATUS / META -->
                  <div class="px-4 sm:px-6 pt-4 pb-5 border-b border-slate-800">
                    <div class="mb-2 text-slate-200 font-medium">Status zamówienia</div>
                    <div class="text-sm text-slate-100 mb-1">
                      <span :class="statusClass">
                        {{ statusLabel }}
                      </span>
                    </div>
                    <p class="text-xs text-slate-200">
                      Utworzone: <span class="font-semibold">{{ createdDateText }}</span><br />
                      Przez: <span class="font-semibold">{{ creatorType }} ({{ creatorName }})</span>
                    </p>
                  </div>

                  <!-- KLIENT -->
                  <div class="px-4 sm:px-6 pt-4 pb-5 border-b border-slate-800">
                    <div class="mb-2 text-slate-200 font-medium">Klient</div>
                    <p class="text-sm text-slate-100 font-semibold">
                      {{ clientName }}
                    </p>
                    <p class="text-xs text-slate-100 mt-1">
                      {{ clientAddress }}
                    </p>
                    <p class="text-xs text-slate-100">
                      NIP: {{ clientNip }}
                    </p>
                    <p class="text-xs text-slate-100 mt-2">
                      E-mail: {{ clientEmail }}<br />
                      Tel.: {{ clientPhone }}
                    </p>
                  </div>

                  <!-- ADRES DOSTAWY -->
                  <div class="px-4 sm:px-6 pt-4 pb-5 border-b border-slate-800">
                    <div class="mb-2 text-slate-200 font-medium">Adres dostawy</div>
                    <p class="text-xs text-slate-100">
                      {{ deliveryAddressLine }}
                    </p>
                    <p class="text-xs text-slate-100 mt-1">
                      Region: <span class="font-semibold">{{ regionName }}</span>
                    </p>

                    <div class="mt-4">
                      <div class="text-sm font-medium text-slate-100">
                        Preferowany termin dostawy
                      </div>
                      <p class="text-xs text-slate-100 mt-1">
                        {{ deliveryDateDisplay }}
                      </p>
                    </div>
                  </div>

                  <!-- FAKTURA / DOSTAWCA -->
                  <div class="px-4 sm:px-6 pt-4 pb-5 border-b border-slate-800">
                    <div class="mb-2 text-slate-200 font-medium">Faktura i dostawca</div>
                    <p class="text-xs text-slate-100">
                      Dostawca: <span class="font-semibold">{{ supplierLabel }}</span>
                    </p>
                    <p class="text-xs text-slate-100 mt-1">
                      Faktura: <span class="font-semibold">{{ invoiceDisplay }}</span>
                    </p>
                  </div>

                  <!-- PALIWO -->
                  <div class="px-4 sm:px-6 pt-4 pb-5">
                    <div class="mb-2 text-slate-200 font-medium">Zamówione paliwo</div>
                    <p class="text-xs text-slate-100">
                      Produkt: <span class="font-semibold">{{ productName }}</span>
                    </p>
                    <p class="text-xs text-slate-100 mt-1">
                      Ilość: <span class="font-semibold">{{ qtyDisplay }}</span>
                    </p>
                  </div>
                </div>

                <!-- FOOTER -->
                <div class="flex flex-shrink-0 justify-end px-4 py-4 items-center">
                  <button
                    @click.prevent="emitClose"
                    class="rounded-md border border-slate-300 bg-white py-2 px-4 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                  >
                    Zamknij
                  </button>
                </div>
              </div>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import {
  Dialog,
  DialogOverlay,
  DialogTitle,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue'

export default {
  name: 'ModalPreviewFuelOrder',
  components: {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot
  },
  props: {
    show: { type: Boolean, default: false },
    order: { type: Object, default: null }
  },
  computed: {
    creatorType() {
      return this.order?.createdByClient ? 'Klient' : 'Obsługa'
    },
    creatorName() {
      return this.order?.meta?.creator?.name || 'Nieznany użytkownik'
    },
    createdDateText() {
      return this.order?.meta?.createdDate?.asText || '—'
    },
    clientName() {
      return this.order?.client?.invoiceAddress?.name || '—'
    },
    clientAddress() {
      const inv = this.order?.client?.invoiceAddress || {}
      const addr = inv.address || ''
      const post = inv.postCode || ''
      const city = inv.city || ''
      return `${addr}${addr ? ', ' : ''}${post} ${city}`.trim() || '—'
    },
    clientNip() {
      return this.order?.client?.invoiceAddress?.nip || '—'
    },
    clientEmail() {
      return this.order?.client?.contact?.email || '—'
    },
    clientPhone() {
      return this.order?.client?.contact?.phone || '—'
    },
    deliveryAddressLine() {
      const d = this.order?.deliveryAddress || {}
      const addr = d.address || ''
      const post = d.postCode || ''
      const city = d.city || ''
      return `${addr}${addr ? ', ' : ''}${post} ${city}`.trim() || '—'
    },
    regionName() {
      return this.order?.deliveryAddress?.regionName || 'Nie przypisano'
    },
    deliveryDateDisplay() {
      const fromText = this.order?.deliveryDate?.from?.asText || null
      const toText = this.order?.deliveryDate?.to?.asText || null
      if (fromText && toText && fromText !== toText) {
        return `${fromText} – ${toText}`
      }
      if (fromText) return fromText
      if (toText) return toText
      return '—'
    },
    supplierLabel() {
      return this.order?.invoice?.isLauronInvoice ? 'Lauron' : 'WBtank'
    },
    invoiceDisplay() {
      if (this.order?.invoice?.invoiced) {
        return this.order.invoice.invoiceNumber || 'Zafakturowano (brak numeru)'
      }
      return 'Oczekuje na fakturę'
    },
    productName() {
      return this.order?.orderedFuel?.fuelName || '—'
    },
    qtyDisplay() {
      const qty = Number(this.order?.orderedFuel?.quantityLiters ?? NaN)
      if (!Number.isFinite(qty)) return '—'
      const nf = new Intl.NumberFormat('pl-PL')
      return `${nf.format(qty)} L`
    },
    statusLabel() {
      const s = this.order?.status
      if (s != null) {
        switch (s) {
          case 1: return 'Oczekuje na przyjęcie'
          case 2: return 'Przyjęte do realizacji'
          case 3: return 'Oczekuje na wyjazd'
          case 4: return 'W trakcie realizacji'
          case 5: return 'Dostarczone'
          case 6: return 'Anulowane'
          default: return 'Nieznany status'
        }
      }
      // Fallback dla starych rekordów
      if (this.order?.canceled) return 'Anulowane'
      if (this.order?.delivered) return 'Dostarczone'
      if (this.order?.acceptedByAdmin) return 'Przyjęte do realizacji'
      return 'Oczekuje na przyjęcie'
    },
    statusClass() {
      const s = this.order?.status
      if (s != null) {
        switch (s) {
          case 1: return 'text-yellow-300'
          case 2: return 'text-blue-300'
          case 3: return 'text-indigo-300'
          case 4: return 'text-purple-300'
          case 5: return 'text-green-300'
          case 6: return 'text-red-300'
          default: return 'text-slate-400'
        }
      }
      if (this.order?.canceled) return 'text-red-300'
      if (this.order?.delivered) return 'text-green-300'
      if (this.order?.acceptedByAdmin) return 'text-blue-300'
      return 'text-yellow-300'
    }
  },
  methods: {
    emitClose() {
      this.$emit('closePreview')
    }
  }
}
</script>
